import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { SITE, WA_MESSAGES } from "../data/site";
import { waLink } from "../lib/whatsapp";
import { scrollToId } from "../lib/scroll";
import { WhatsAppButton } from "./Buttons";

const LINES = [
    { text: "GET FOUND.", accent: false },
    { text: "GET BOOKED.", accent: false },
    { text: "GET GROWING.", accent: true },
];

export const Hero = () => {
    const reduce = useReducedMotion();
    const { scrollY } = useScroll();
    const logoY = useTransform(scrollY, [0, 600], [0, reduce ? 0 : 110]);
    const arrowY = useTransform(scrollY, [0, 600], [0, reduce ? 0 : -70]);

    return (
        <section className="girder-grid relative overflow-hidden pb-16 pt-32 sm:pb-24 sm:pt-40" data-testid="hero-section">
            <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:px-16">
                <div>
                    <motion.p
                        initial={reduce ? false : { opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand-orange"
                        data-testid="hero-eyebrow"
                    >
                        <MapPin size={14} aria-hidden="true" />
                        033 Digital — Kolkata
                    </motion.p>

                    <h1 className="mt-6 font-display text-4xl font-bold leading-[1.04] tracking-tight text-brand-brown sm:text-5xl lg:text-6xl">
                        {LINES.map((line, i) => (
                            <span key={line.text} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${line.accent ? "italic text-brand-orange" : ""}`}
                                    initial={reduce ? false : { y: "115%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={reduce ? false : { opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.65 }}
                        className="mt-6 max-w-xl text-sm leading-relaxed text-brand-muted sm:text-base"
                        data-testid="hero-subtitle"
                    >
                        AI-powered digital growth systems for local businesses. We connect how customers find you, contact
                        you, book you, review you and come back — so marketing stops being scattered tactics and starts
                        working as one system.
                    </motion.p>

                    <motion.div
                        initial={reduce ? false : { opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.8 }}
                        className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
                    >
                        <button
                            type="button"
                            onClick={() => scrollToId("scan")}
                            data-testid="hero-scan-button"
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-brand-orange-dark sm:text-base"
                        >
                            Business Scan
                            <ArrowUpRight size={18} aria-hidden="true" />
                        </button>
                        <WhatsAppButton href={waLink(WA_MESSAGES.grow)} testid="hero-whatsapp-button" />
                    </motion.div>

                    <motion.p
                        initial={reduce ? false : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1 }}
                        className="mt-8 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand-muted"
                    >
                        <span className="inline-block h-2 w-2 animate-pulse-dot rounded-full bg-brand-orange" aria-hidden="true" />
                        Marketing that grows businesses
                    </motion.p>
                </div>

                <div className="relative hidden lg:block" aria-hidden="true">
                    <motion.div
                        style={{ y: arrowY }}
                        className="absolute -top-8 right-4 text-brand-orange"
                        initial={reduce ? false : { opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                    >
                        <ArrowUpRight size={120} strokeWidth={1.4} />
                    </motion.div>
                    <motion.div
                        style={{ y: logoY }}
                        initial={reduce ? false : { opacity: 0, y: 40, rotate: 0 }}
                        animate={{ opacity: 1, rotate: -2 }}
                        transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="animate-float-soft relative ml-auto w-full max-w-sm rounded-3xl border border-brand-brown/10 bg-white p-5 shadow-[0_30px_60px_-20px_rgba(53,24,13,0.25)]"
                    >
                        <img
                            src={SITE.logoFull}
                            alt=""
                            className="w-full rounded-2xl"
                            width="400"
                            height="400"
                            loading="eager"
                        />
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
